import { useMemo } from 'react';

import { useTranslate } from 'src/locales';
import { useAuthContext } from 'src/auth/hooks';

import { SvgColor } from 'src/components/svg-color';

import { paths } from 'src/routes/paths';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  board: icon('ic-nav-board'),
  user: icon('ic-nav-user'),
  userContact: icon('ic-nav-user-contact'),
  organize: icon('ic-nav-organize'),
  node: icon('ic-nav-node'),
  articleCategory: icon('ic-nav-article-category'),
  article: icon('ic-nav-article'),
  order: icon('ic-nav-order'),
  token: icon('ic-nav-token'),
  quest: icon('ic-nav-quest'),
  questReward: icon('ic-nav-quest-reward'),
  reward: icon('ic-nav-reward'),
  redeam: icon('ic-nav-redeam'),
  garbageType: icon('ic-nav-garbage-type'),
  garbage: icon('ic-nav-garbage'),
  log: icon('ic-nav-log'),
  garbageByView: icon('ic-nav-garbage-by-view'),
  garbageByType: icon('ic-nav-garbage-by-type'),
  location: icon('ic-nav-location-type'),
  locationType: icon('ic-nav-location'),
  theme: icon('ic-nav-theme'),
  banner: icon('ic-nav-banner'),
  logout: icon('ic-nav-logout'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t: tSidebar } = useTranslate('sidebar');
  const { user: currentUser } = useAuthContext();

  const data = useMemo(() => {
    const allNavData = [
      {
        subheader: {
          title: tSidebar('dashboard.title'),
        },
        items: [
          {
            title: tSidebar('dashboard.title'),
            path: paths.dashboard.general.dashboardOverview,
            icon: ICONS.board,
          },
          {
            title: tSidebar('dashboard.report'),
            path: paths.dashboard.general.report,
            icon: ICONS.order,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
          },
          {
            title: tSidebar('dashboard.user'),
            path: paths.dashboard.user.root,
            icon: ICONS.user,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
          },
          {
            title: tSidebar('dashboard.userContact'),
            path: paths.dashboard.userContact.root,
            icon: ICONS.userContact,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
          },
          {
            title: tSidebar('dashboard.node'),
            path: paths.dashboard.node.root,
            icon: ICONS.node,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.organize'),
            path: paths.dashboard.organize.root,
            icon: ICONS.organize,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.articleCategory'),
            path: paths.dashboard.article.category,
            icon: ICONS.articleCategory,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.article'),
            path: paths.dashboard.article.root,
            icon: ICONS.article,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.token'),
            path: paths.dashboard.token.root,
            icon: ICONS.token,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.order'),
            path: paths.dashboard.order.root,
            icon: ICONS.order,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.quest'),
            path: paths.dashboard.quest.root,
            icon: ICONS.quest,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.questReward'),
            path: paths.dashboard.questReward.root,
            icon: ICONS.questReward,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.reward'),
            path: paths.dashboard.reward.root,
            icon: ICONS.reward,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.redeam'),
            path: paths.dashboard.redeam.root,
            icon: ICONS.redeam,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.garbageType'),
            path: paths.dashboard.garbage.garbage_type,
            icon: ICONS.garbageType,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('dashboard.garbage'),
            path: paths.dashboard.garbage.root,
            icon: ICONS.garbage,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
        ],
      },
      {
        subheader: {
          title: tSidebar('garbageCollection.title'),
          roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
        },
        items: [
          {
            title: tSidebar('garbageCollection.logList'),
            path: paths.garbageCollection.garbageLog.root,
            icon: ICONS.log,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['DISTRICT_OFFICE'],
          },
          {
            title: tSidebar('garbageCollection.manageBy'),
            path: paths.garbageCollection.garbageManageBy.root,
            icon: ICONS.garbageByView,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['DISTRICT_OFFICE'],
          },
          {
            title: tSidebar('garbageCollection.manageType'),
            path: paths.garbageCollection.garbageManageType.root,
            icon: ICONS.garbageByType,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['DISTRICT_OFFICE'],
          },
          {
            title: tSidebar('garbageCollection.location'),
            path: paths.garbageCollection.location.root,
            icon: ICONS.location,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['DISTRICT_OFFICE'],
          },
          {
            title: tSidebar('garbageCollection.locationType'),
            path: paths.garbageCollection.locationType.root,
            icon: ICONS.locationType,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['DISTRICT_OFFICE'],
          },
        ],
      },
      {
        subheader: {
          title: tSidebar('management.title'),
          roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
        },
        items: [
          {
            title: tSidebar('management.theme'),
            path: paths.management.theme.root,
            icon: ICONS.theme,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE', 'DISTRICT_OFFICE'],
          },
          {
            title: tSidebar('management.banner'),
            path: paths.management.banner.root,
            icon: ICONS.banner,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE'],
          },
          {
            title: tSidebar('management.invitationCode'),
            path: paths.management.invitationCode.root,
            icon: ICONS.organize,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE', 'DISTRICT_OFFICE'],
          },
          {
            title: tSidebar('management.invitationCodeLog'),
            path: paths.management.invitationCodeLog.root,
            icon: ICONS.log,
            roles: ['MANAGER', 'ADMIN', 'SYSTEM'],
            type: ['ORGANIZE', 'DISTRICT_OFFICE'],
          },
        ],
      },
      {
        subheader: {
          title: tSidebar('system.title'),
        },
        items: [
          {
            title: tSidebar('system.signout'),
            path: '/logout',
            icon: ICONS.logout,
          },
        ],
      },
    ];

    // Filter items based on currentUser.organizeData.type
    return allNavData
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          // If item has no type property, show it for all types
          if (!item.type) return true;
          // If item has type property, check if currentUser.organizeData.type is included
          return item.type.includes(currentUser?.organizeData?.type || '');
        }),
      }))
      .filter((section) => section.items.length > 0); // Remove empty sections
  }, [tSidebar, currentUser?.organizeData?.type]);

  return data;
}
